/* eslint-disable react/jsx-wrap-multilines */
import { ChangeEvent, useContext, useEffect, useState } from 'react';

import { Text } from '@gympass/yoga';
import { ArrowLeft, Search } from '@gympass/yoga-icons';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FeatureToggle } from 'utils/unleash/toggles';

import { SpecialistContext } from 'context';
import { getSpecialistRole } from 'context/SpecialistContext/SpecialistContext';
import { SpecialistRole } from 'services';

import { MessageTipsDrawer } from 'components/MessageTipsDrawer/MessageTipsDrawer';

import { selectableFilters } from './const';

import { ChatListProps, ListTab } from './types';

import * as S from './styles';

const ChatList: React.FC<ChatListProps> = ({
  title,
  listTab,
  children,
  onTabClick,
  showArrowButton,
  onFilterPatients,
  onClickNewMessage,
  onClickArrowButton,
  filterValue,
  selectedFilter,
  onSelectFilter,
  id = 'scrollableDiv',
  hideNewMessageButton = false,
  hideTipsButton = false,
  ...props
}) => {
  const [searchedValue, setSearchedValue] = useState(filterValue);
  const [showMessageTipsDrawer, setShowMessageTipsDrawer] = useState(false);
  const [iconDescriptionTooltipIsOpen, setIconDescriptionTooltipIsOpen] =
    useState(false);

  const intl = useIntl();
  const suggestedMessagesEnabled = useFlag(FeatureToggle.SuggestedMessagesV1);

  const { specialistInfo } = useContext(SpecialistContext);
  const specialistRole = getSpecialistRole(specialistInfo?.groups);

  useEffect(() => {
    setSearchedValue(filterValue);
  }, [filterValue]);

  const handleShowIconDescriptionTooltip = () => {
    setIconDescriptionTooltipIsOpen(true);
  };

  const handleHideIconDescriptionTooltip = () => {
    setIconDescriptionTooltipIsOpen(false);
  };

  const handlePatientFilter = async (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchedValue(value);

    onFilterPatients(value);
  };

  const handleShowMessageTipsDrawer = () => {
    setShowMessageTipsDrawer(true);
  };

  const handleCloseMessageTipsDrawer = () => {
    setShowMessageTipsDrawer(false);
  };

  const showNewMessageButton =
    specialistRole !== SpecialistRole.TC && !hideNewMessageButton;
  const showMessageTipsButton =
    specialistRole !== SpecialistRole.TC &&
    suggestedMessagesEnabled &&
    !hideTipsButton;
  const showSelectableFilters = specialistRole !== SpecialistRole.TC;

  const placeholder = intl.formatMessage({ id: 'CHAT.SEARCH.PLACEHOLDER' });

  return (
    <>
      <S.ChatListRoot data-testid="contentChatList" {...props}>
        <S.ChatListHeader>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <S.ChatListHeaderContainer
              className={showArrowButton ? 'align-left' : ''}
            >
              {showArrowButton && (
                <S.ArrowButton
                  data-testid="arrowButton"
                  onClick={onClickArrowButton}
                >
                  <ArrowLeft width={26} height={26} />
                </S.ArrowButton>
              )}
              <Text.H4 data-testid="chatListTitle" light>
                {title}
              </Text.H4>
            </S.ChatListHeaderContainer>
            <S.Tooltip
              open={iconDescriptionTooltipIsOpen}
              onOpen={handleShowIconDescriptionTooltip}
              onClose={handleHideIconDescriptionTooltip}
              title={
                <>
                  <Box
                    bgcolor="white"
                    px={2}
                    py={1}
                    height={112}
                    display="flex"
                    flexDirection="column"
                    sx={{ boxShadow: 3 }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      gap={2}
                      height={32}
                    >
                      <S.ScheduledMessageIcon />
                      <Typography variant="body2">
                        {intl.formatMessage({
                          id: 'CPRESPONSIVE.SCHEDULEDSTATUS.TOOLTIP',
                        })}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      gap={2}
                      height={32}
                    >
                      <S.UnlinkIcon />
                      <Typography variant="body2">
                        {intl.formatMessage({
                          id: 'CPRESPONSIVE.UNLINKSTATUS.TOOLTIP',
                        })}
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      gap={2}
                      height={32}
                    >
                      <S.CriticalityIcon />
                      <Typography variant="body2">
                        {intl.formatMessage({
                          id: 'CPRESPONSIVE.CRITICALSTATUS.TOOLTIP',
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </>
              }
            >
              <S.IconLegendIcon />
            </S.Tooltip>
          </Box>
          <S.HeaderActions>
            {showNewMessageButton && (
              <Button
                variant="contained"
                onClick={onClickNewMessage}
                size="small"
                data-testid="newMessageButton"
                fullWidth
              >
                <FormattedMessage id="CHAT.PAGE.CONVERSATION.EMPTY_STATE.BUTTON.NEW_MESSAGE" />
              </Button>
            )}

            {showMessageTipsButton && (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={handleShowMessageTipsDrawer}
              >
                <S.MessageTipsIcon />
                {intl.formatMessage({ id: 'MESSAGETIPS.SCHEDULE.BUTTONUP' })}
              </Button>
            )}
          </S.HeaderActions>
          <S.ChatListHeaderDivider />
          <S.PatientNameInput
            icon={<Search />}
            placeholder={placeholder}
            data-testid="chatListFilter"
            onChange={handlePatientFilter}
            value={searchedValue || filterValue}
          />
        </S.ChatListHeader>
        {listTab && (
          <S.ChatListTabs data-testid="chatListTabs">
            <S.ChatListTab
              role="button"
              title={intl.formatMessage({ id: 'CHAT.LIST.TAB.ACTIVE' })}
              aria-label={intl.formatMessage({ id: 'CHAT.LIST.TAB.ACTIVE' })}
              isSelected={listTab === ListTab.EU_ACTIVE}
              onClick={() => {
                if (listTab === ListTab.EU_ACTIVE) return;
                if (onTabClick) onTabClick(ListTab.EU_ACTIVE);
              }}
            >
              <Text.SectionTitle>
                <FormattedMessage id="CHAT.LIST.TAB.ACTIVE" />
              </Text.SectionTitle>
            </S.ChatListTab>
            <S.ChatListTab
              role="button"
              title={intl.formatMessage({ id: 'CHAT.LIST.TAB.NOT_STARTED' })}
              aria-label={intl.formatMessage({
                id: 'CHAT.LIST.TAB.NOT_STARTED',
              })}
              isSelected={listTab === ListTab.EU_NOT_STARTED}
              onClick={() => {
                if (listTab === ListTab.EU_NOT_STARTED) return;
                if (onTabClick) onTabClick(ListTab.EU_NOT_STARTED);
              }}
            >
              <Text.SectionTitle>
                <FormattedMessage id="CHAT.LIST.TAB.NOT_STARTED" />
              </Text.SectionTitle>
            </S.ChatListTab>
          </S.ChatListTabs>
        )}
        {showSelectableFilters && (
          <S.SelectableFiltersWrapper>
            {selectableFilters.map(({ listFilter, label }) => {
              const ChipComponent =
                selectedFilter === listFilter ? S.SelectedFilterChip : Chip;
              return (
                <ChipComponent
                  key={label}
                  size="small"
                  label={intl.formatMessage({ id: label })}
                  onClick={() => {
                    onSelectFilter?.(listFilter);
                  }}
                />
              );
            })}
          </S.SelectableFiltersWrapper>
        )}
        <S.ChatListContainer hasListTabs={!!listTab} id={id}>
          {children}
        </S.ChatListContainer>
      </S.ChatListRoot>

      {showMessageTipsDrawer && (
        <MessageTipsDrawer
          isOpen={showMessageTipsDrawer}
          onClose={handleCloseMessageTipsDrawer}
        />
      )}
    </>
  );
};

export default ChatList;
